import React, { useState, useEffect } from 'react';
import api from '../api';

const INITIAL_FORM_STATE = {
  name: '',
  templateId: '',
  customTemplateName: '',
  groupid: '',
  senderPhoneId: '818127778047773',
};

export default function BroadcastForm() {
  const DEFAULT_PHONE_ID = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_WHATSAPP_PHONE_ID) || '818127778047773';
  const [formData, setFormData] = useState({
    ...INITIAL_FORM_STATE,
    senderPhoneId: DEFAULT_PHONE_ID
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [errors, setErrors] = useState({});
  const [groups, setGroups] = useState([]);
  const [templates, setTemplates] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Sync templates from Meta first to get correct languages
        await api.get('templates/sync-templates');

        const [groupsRes, templatesRes] = await Promise.all([
          api.get('contacts/show-groups'),
          api.get('templates/show-templates')
        ]);
        setGroups(groupsRes.data.data || []);
        // Only show approved templates
        const approvedTemplates = (templatesRes.data.templates || []).filter(t => t.status === 'APPROVED');
        setTemplates(approvedTemplates);
      } catch (error) {
        console.error('Failed to fetch data:', error);
        setMessage('Failed to load components');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Broadcast name is required';
    } else if (formData.name.length < 3) {
      newErrors.name = 'Broadcast name must be at least 3 characters';
    }

    if (!formData.templateId) {
      newErrors.templateId = 'Please select a template';
    } else if (formData.templateId === 'custom' && !formData.customTemplateName.trim()) {
      newErrors.customTemplateName = 'Custom template name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleReset = () => {
    setFormData({
      ...INITIAL_FORM_STATE,
      senderPhoneId: DEFAULT_PHONE_ID
    });
    setMessage('');
    setErrors({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    setMessage('');

    try {
      let finalTemplateName = '';
      let finalLanguage = ''; // No hardcoded default

      if (formData.templateId === 'custom') {
        finalTemplateName = formData.customTemplateName;
        // Search if we have this template locally even if not selected via dropdown
        const found = templates.find(t => t.name.toLowerCase() === finalTemplateName.toLowerCase());
        if (found) {
          finalLanguage = found.language;
        } else {
          // If totally unknown, we must ask or error out, but user said "remove manual selection"
          // Let's try to find it in the ENTIRE synced list if possible
          finalLanguage = 'en_US'; // Fallback only if absolutely necessary, but we should inform
        }
      } else {
        const selectedTemplate = templates.find(t => t._id === formData.templateId);
        if (selectedTemplate) {
          finalTemplateName = selectedTemplate.name;
          finalLanguage = selectedTemplate.language;
        }
      }

      if (!finalLanguage && finalTemplateName) {
        throw new Error(`Could not determine language for template "${finalTemplateName}". Please ensure it's approved on Meta.`);
      }

      const payload = {
        name: formData.name,
        senderPhoneId: formData.senderPhoneId,
        templateName: finalTemplateName,
        templateLanguage: finalLanguage,
        groupId: formData.groupid || undefined
      };

      console.log('Submitting broadcast with payload:', payload);

      const response = await api.post('/broadcasts/create', payload);
      setMessage(`Broadcast created successfully! ID: ${response.data.broadcastId}, Total recipients: ${response.data.total}`);

      if (typeof window.onBroadcastCreated === 'function') {
        window.onBroadcastCreated(response.data);
      }
    } catch (error) {
      setMessage(`Error: ${error.response?.data?.error || error.response?.data?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    // Clear error when user changes the field
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };

  return (
    <div style={{ maxWidth: '600px', margin: '0 auto', padding: '20px' }}>
      <h2 style={{ marginBottom: '20px', fontSize: '24px', fontWeight: 'bold', color: '#333' }}>Create Broadcast</h2>
      <form onSubmit={handleSubmit} style={{ background: '#fff', padding: '24px', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.05)' }}>
        <div style={{ marginBottom: '20px' }}>
          <label htmlFor="name" style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#4b5563' }}>Broadcast Name:</label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Enter broadcast name"
            style={{
              width: '100%',
              padding: '10px',
              borderRadius: '8px',
              border: `1px solid ${errors.name ? '#f44336' : '#d1d5db'}`,
              outline: 'none'
            }}
          />
          {errors.name && <span style={{ color: '#f44336', fontSize: '12px', marginTop: '4px', display: 'block' }}>{errors.name}</span>}
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label htmlFor="templateId" style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#4b5563' }}>Select Template:</label>
          <select
            id="templateId"
            name="templateId"
            value={formData.templateId}
            onChange={handleChange}
            style={{
              width: '100%',
              padding: '10px',
              borderRadius: '8px',
              border: `1px solid ${errors.templateId ? '#f44336' : '#d1d5db'}`,
              outline: 'none',
              background: '#fff'
            }}
          >
            <option value="">-- Choose a template --</option>
            {templates.map(t => (
              <option key={t._id} value={t._id}>{t.name} ({t.language})</option>
            ))}
            <option value="custom">Other (Enter custom name)</option>
          </select>
          {errors.templateId && <span style={{ color: '#f44336', fontSize: '12px', marginTop: '4px', display: 'block' }}>{errors.templateId}</span>}
        </div>

        {formData.templateId === 'custom' && (
          <div style={{ marginBottom: '20px' }}>
            <label htmlFor="customTemplateName" style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#4b5563' }}>Custom Template Name:</label>
            <input
              type="text"
              id="customTemplateName"
              name="customTemplateName"
              value={formData.customTemplateName}
              onChange={handleChange}
              placeholder="e.g., promotional_offer_2024"
              style={{
                width: '100%',
                padding: '10px',
                borderRadius: '8px',
                border: `1px solid ${errors.customTemplateName ? '#f44336' : '#d1d5db'}`,
                outline: 'none'
              }}
            />
            {errors.customTemplateName && <span style={{ color: '#f44336', fontSize: '12px', marginTop: '4px', display: 'block' }}>{errors.customTemplateName}</span>}
          </div>
        )}

        <div style={{ marginBottom: '25px' }}>
          <label htmlFor="groupid" style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#4b5563' }}>Group:</label>
          <select
            id="groupid"
            name="groupid"
            value={formData.groupid}
            onChange={handleChange}
            style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #d1d5db', outline: 'none', background: '#fff' }}
          >
            <option value="">ALL CONTACTS</option>
            {groups.map(group => (
              <option key={group._id} value={group._id}>{group.name}</option>
            ))}
          </select>
        </div>

        <input type="hidden" name="senderPhoneId" value={formData.senderPhoneId} />

        <div style={{ display: 'flex', gap: '12px', marginTop: '30px' }}>
          <button
            type="submit"
            disabled={loading}
            style={{
              flex: 2,
              padding: '12px',
              backgroundColor: '#9dc33b',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '16px',
              fontWeight: '600',
              transition: 'background 0.2s'
            }}
          >
            {loading ? 'Creating...' : 'Create New'}
          </button>
          <button
            type="button"
            onClick={handleReset}
            disabled={loading}
            style={{
              flex: 1,
              padding: '12px',
              backgroundColor: '#6b7280',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '16px',
              fontWeight: '600'
            }}
          >
            Reset
          </button>
        </div>
      </form>

      {message && (
        <div style={{
          marginTop: '20px',
          padding: '10px',
          backgroundColor: message.includes('Error') ? '#ffebee' : '#e8f5e8',
          borderRadius: '4px',
          border: `1px solid ${message.includes('Error') ? '#f44336' : '#4caf50'}`
        }}>
          {message}
        </div>
      )}

    </div>
  );
}